/** @jsxImportSource @emotion/react */
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createPath,
  type NavigateOptions,
  type To,
  useLocation,
  useNavigate,
} from 'react-router';

import {
  ModalActions,
  ModalBody,
  ModalCancelButton,
  ModalCard,
  ModalCloseButton,
  ModalDivider,
  ModalSuccessButton,
  ModalTitle,
} from '@/Client/Widgets/Modal/ModalLib';
import { ModalShell as ModalShellPrimitive } from '@/Client/Widgets/Modal/ModalShell';

import { leavePromptSuppressed } from '@/Client/Lib/leavePromptLib';
import { type NavigationGuard, NavigationGuardContext } from './NavigationGuardContext';
import { setGlobalNavigate } from './navigationRef';

const TITLE_ID = 'navigation-guard-unsaved-changes-title';

type PendingNavigation = {
  to: To;
  options?: NavigateOptions;
};

const UnsavedChangesNavigationModal = ({
  visible,
  onClose,
  onCancelChanges,
  onSaveChanges,
}: {
  visible: boolean;
  onClose: () => void;
  onCancelChanges: () => void | Promise<void>;
  onSaveChanges: () => void | Promise<void>;
}) => (
  <ModalShellPrimitive visible={visible} onClose={onClose} contentMaxWidth='560px'>
    <div role='dialog' aria-modal='true' aria-labelledby={TITLE_ID}>
      <ModalCard>
        <ModalCloseButton
          aria-label='Close unsaved changes modal'
          onClick={onClose}
        />
        <ModalTitle id={TITLE_ID}>Unsaved Changes</ModalTitle>
        <ModalBody>
          You have unsaved changes. Save them before leaving, discard them and continue, or cancel to keep editing.
        </ModalBody>
        <ModalDivider />
        <ModalActions>
          <ModalCancelButton muted onClick={onClose}>
            Cancel
          </ModalCancelButton>
          <ModalCancelButton onClick={onCancelChanges}>
            Discard Changes
          </ModalCancelButton>
          <ModalSuccessButton onClick={onSaveChanges}>
            Save Changes
          </ModalSuccessButton>
        </ModalActions>
      </ModalCard>
    </div>
  </ModalShellPrimitive>
);

export const NavigationGuardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationGuardRef = useRef<NavigationGuard | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);

  const setNavigationGuard = useCallback((guard: NavigationGuard | null) => {
    navigationGuardRef.current = guard;
  }, []);

  const closeUnsavedChangesModal = useCallback(() => {
    setPendingNavigation(null);
  }, []);

  const guardedNavigate = useCallback(
    (to: To, options?: NavigateOptions) => {
      const path = typeof to === 'string' ? to : createPath(to);
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      if (path === currentPath) return;

      if (navigationGuardRef.current?.hasChanges) {
        setPendingNavigation({ to, options });
        return;
      }

      navigate(to, options);
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  // Keeps navigationRef's plain, hook-free navigateTo() in sync with the
  // guard-aware navigate this provider computes - the bridge for
  // non-component code (e.g. mainNavigationConfig.tsx) that needs to
  // navigate without being able to call useNavigate() itself.
  useEffect(() => {
    setGlobalNavigate(guardedNavigate);
  }, [guardedNavigate]);

  // Browser-level navigation (reload, tab close, external URL): the browser
  // only allows its own generic confirmation, so trigger that while a guard
  // has unsaved changes. In-app back/forward is not interceptable without a
  // data router, so it remains unguarded.
  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!navigationGuardRef.current?.hasChanges || leavePromptSuppressed()) return;
      event.preventDefault();
      // Required by Chrome for the confirmation prompt to appear.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, []);

  const saveChangesAndNavigate = useCallback(async () => {
    const navigationGuard = navigationGuardRef.current;
    if (!pendingNavigation || !navigationGuard) return;

    const saved = await navigationGuard.saveChanges();
    if (!saved) return;

    setNavigationGuard(null);
    setPendingNavigation(null);
    navigate(pendingNavigation.to, pendingNavigation.options);
  }, [navigate, pendingNavigation, setNavigationGuard]);

  const cancelChangesAndNavigate = useCallback(async () => {
    const navigationGuard = navigationGuardRef.current;
    if (!pendingNavigation || !navigationGuard) return;

    await navigationGuard.discardChanges();
    setNavigationGuard(null);
    setPendingNavigation(null);
    navigate(pendingNavigation.to, pendingNavigation.options);
  }, [navigate, pendingNavigation, setNavigationGuard]);

  const contextValue = useMemo(
    () => ({ setNavigationGuard, guardedNavigate }),
    [guardedNavigate, setNavigationGuard],
  );

  return (
    <NavigationGuardContext.Provider value={contextValue}>
      {children}
      <UnsavedChangesNavigationModal
        visible={pendingNavigation !== null}
        onClose={closeUnsavedChangesModal}
        onCancelChanges={cancelChangesAndNavigate}
        onSaveChanges={saveChangesAndNavigate}
      />
    </NavigationGuardContext.Provider>
  );
};
